/** Aksiyonlar (Tüm Aksiyonlar) tipleri (044/6.4, no-any). Backend ActionListItemDto ile birebir. */

import { todayStart } from '../../lib/datetime'

/** MEETING_ACTION_STATUS (REFERANS) id'leri. */
export const ACTION_STATUS = {
  PENDING: 330,
  /** 331 — Tamamlandı. Ana aksiyonda geri alınabilir ara durum; alt aksiyonda SON durum. */
  COMPLETED: 331,
  /**
   * 332 — Devam Ediyor. **Hiçbir kümede yoktur** (22.09.2026): alt görev statüleri ana görevle
   * eşitlenirken kalktı. Sabit duruyor çünkü DB'de bu değeri taşıyan kayıtlar var ve rozetleri
   * renksiz kalmamalı; veri düzeltmesini analist yapar.
   */
  IN_PROGRESS: 332,
  /** 333 — Kapatıldı. Ana görevde arşivlemenin kaynağı; alt görevde SON durum. */
  CLOSED: 333,
  ARCHIVED: 339,
} as const

/** Ana aksiyonun statü kümesi (085/FR-001). */
export const MAIN_ACTION_STATUSES: number[] = [
  ACTION_STATUS.PENDING,
  ACTION_STATUS.COMPLETED,
  ACTION_STATUS.CLOSED,
  ACTION_STATUS.ARCHIVED,
]

/**
 * Alt görevin statü kümesi — 22.09.2026'da **ana görevinkiyle eşitlendi** (kullanıcı kararı): 332 Devam
 * Ediyor kalktı, 333 Kapatıldı eklendi. **Arşivlendi YOKTUR**: arşivleme yalnız ana görevin yaşam
 * döngüsüdür, alt görevde Kapatıldı son durumdur. Sunucudaki `SUBACTION_STATUSES` ile birebir.
 */
export const SUBACTION_STATUSES: number[] = [
  ACTION_STATUS.PENDING,
  ACTION_STATUS.COMPLETED,
  ACTION_STATUS.CLOSED,
]

/** Statü seçeneği — REFERANS(MEETING_ACTION_STATUS)'tan dinamik gelir (id + dile-duyarlı ad). */
export interface ActionStatusOption {
  id: number
  name: string
}

/**
 * Aksiyon durumu (MEETING_ACTION_STATUS id) → i18n anahtarı. Statü ADI normalde sunucudan (REFERANS)
 * gelir; bu harita yalnız sunucunun ad döndürmediği yerler (arşiv filtresi seçenekleri, rozetin
 * yedeği) içindir — iş mantığı taşımaz.
 */
export const STATUS_KEY: Record<number, string> = {
  [ACTION_STATUS.PENDING]: 'actions.status.pending',
  [ACTION_STATUS.COMPLETED]: 'actions.status.completed',
  [ACTION_STATUS.IN_PROGRESS]: 'actions.status.inProgress',
  [ACTION_STATUS.CLOSED]: 'actions.status.closed',
  [ACTION_STATUS.ARCHIVED]: 'actions.status.archived',
}

/**
 * 085 — gecikmeli statü ÇEŞİDİ kalktığı için temel-statü indirgemesi ARTIK YOKTUR; fonksiyon
 * geriye uyumluluk için kimlik dönüşümüdür (çağrı yerleri tek tek temizlenene kadar).
 */
export function baseStatusOf(sid: number | null): number | null {
  return sid
}

/**
 * Ham aksiyon STATÜSÜ (MEETING_ACTION_STATUS) → renk <b>ailesi</b> tonu. Güncel eşleme (085 kataloğu):
 * <b>Aksiyon Bekleniyor / Devam Ediyor = Mavi</b> (330 · 332), <b>Tamamlandı = Yeşil</b> (331 · 337),
 * <b>Kapatıldı = Turuncu</b> (333 — kullanıcı kararı 2026-08-28; önceden nötr/griydi),
 * <b>Arşivlendi/İptal = nötr</b> (gri). Gecikmeli (`*Late`) yuvalar aynı hue'nun <b>koyu/dolu</b> tonudur;
 * ham statüde artık kullanılmaz, pano kovalarında ({@link BUCKET_TONE}) yaşar.
 *
 * <p>Kapatıldı'nın turuncusu <b>kendi yuvasındadır</b> (`closed`, `--st-closed-*`) — pano kovalarının
 * kullandığı `updated` turuncusuyla ORTAK DEĞİLDİR. Ayrı tutulmasının sebebi: rozet ile Toplantı kartı
 * çubuğunun tonları birbirine yaklaştırıldı (2026-08-28) ve bu ayar `updated` yuvasında yapılsaydı pano
 * grafiklerinin "Tamamlandı" sütunları da kayardı.
 *
 * <p>Rozet ({@link ActionStatusTag}) ve Toplantı kartı dağılım çubuğu (GroupActionStats) bu tek kaynaktan
 * beslenir — renk buradan değiştirilir, iki yerde ayrı tanım yoktur.
 */
export type StatusTone =
  | 'ongoing'
  | 'ongoingLate'
  | 'updated'
  | 'updatedLate'
  | 'completed'
  | 'completedLate'
  | 'closed'
  | 'neutral'

const STATUS_TONE_BY_ID: Record<number, StatusTone> = {
  330: 'ongoing', // Aksiyon Bekleniyor
  332: 'ongoing', // Devam Ediyor (alt aksiyon) — gecikme artık statü değil, ayrı etiket
  331: 'completed', // Tamamlandı
  333: 'closed', // Kapatıldı — turuncu (2026-08-28)
  339: 'neutral', // Arşivlendi
  // Aşağıdakiler 2026-08-13'te kullanımdan kalktı; MEVCUT kayıtlar hâlâ taşıyor ve rozetleri
  // renksiz kalmasın diye eşleme korunur (yeni kayıt bu değerleri almaz).
  336: 'updated',
  337: 'completed',
  334: 'neutral', // eski İptal
}

/** Statü id → aile tonu; eşlemesi olmayan/boş statü nötr (`neutral`). */
export function statusTone(statusId: number | null | undefined): StatusTone {
  if (statusId == null) return 'neutral'
  return STATUS_TONE_BY_ID[statusId] ?? 'neutral'
}

/** Aile tonu → dağılım çubuğu (dolu/orta) renk değişkeni; rozet zemini CSS sınıfındadır (`.act-badge--*`). */
export const STATUS_TONE_COLOR_VAR: Record<StatusTone, string> = {
  ongoing: 'var(--st-ongoing-mid)',
  ongoingLate: 'var(--st-ongoing-late-mid)',
  updated: 'var(--st-updated-mid)',
  updatedLate: 'var(--st-updated-late-mid)',
  completed: 'var(--st-completed-mid)',
  completedLate: 'var(--st-completed-late-mid)',
  closed: 'var(--st-closed-mid)',
  neutral: 'var(--act-neutral)',
}

/**
 * Anasayfa panolarının türetilmiş kovası (backend `bucket`) → paletteki renk <b>ailesi</b>
 * ({@link statusTone} ile AYNI palet, mockup 2026-08-13). Panonun üç ana durumu üç ayrık hue alır:
 *
 * <ul>
 *   <li><b>Aksiyon Bekleniyor = Mavi</b> (`ongoing` ailesi)
 *   <li><b>Tamamlandı = Turuncu</b> (`updated` ailesi)
 *   <li><b>Kapatıldı = Yeşil</b> (`completed` ailesi)
 * </ul>
 *
 * <p><b>Ton adları palet YUVASIDIR, statü adı değildir.</b> `updated`/`completed` adları 2026-08-06
 * kataloğundan kalmadır (o gün "Güncellendi" turuncu, "Tamamlandı" yeşildi); 085 ile statüler yeniden
 * adlandırıldığı için ad ile anlam ayrıştı. Yuvaların adını değiştirmek uygulama genelindeki rozet
 * sınıflarını (`.act-badge--*`) da kırardı; bu yüzden eşleme <b>renk</b> düzeyinde okunur:
 * mavi → bekleyen, turuncu → tamamlanan, yeşil → kapatılan.
 *
 * <p><b>Gecikmiş kırılım aynı hue'nun KOYU tonudur</b> (`*Late`). Mockup'ta gecikmiş sütunlar açık tonda
 * çizilmişti; koyu ton bilinçli olarak korunur — uygulamanın her yerindeki gecikme göstergesi (rozet,
 * toplantı kartı çubuğu) koyu tondur ve açık ton hem o dili bozar hem beyaz zeminde okunaklılığı düşürür.
 */
/**
 * Statü **tonu → rozet sınıfı**. Rozetin görünümü tek yerde tanımlıdır (`actions.css` → `.act-badge--*`);
 * bu sözlük onu kullanan her yüzeyin ortak kaynağıdır: aksiyon listeleri/detayı ({@link
 * ActionStatusTag}, tonu statü kimliğinden çözer) ve anasayfa panosunun "Durum" kolonu (tonu
 * {@link BUCKET_TONE} ile **kovadan** çözer, bu yüzden o bileşeni çağıramaz). İkinci bir kopya
 * tutulsaydı iki yüzey aynı durumu farklı renkte gösterebilirdi.
 */
export const STATUS_TONE_BADGE_CLASS: Record<StatusTone, string> = {
  ongoing: 'act-badge--ongoing',
  ongoingLate: 'act-badge--ongoing-late',
  updated: 'act-badge--updated',
  updatedLate: 'act-badge--updated-late',
  completed: 'act-badge--completed',
  completedLate: 'act-badge--completed-late',
  closed: 'act-badge--closed',
  neutral: 'act-badge--cancelled',
}

export const BUCKET_TONE: Record<string, StatusTone> = {
  PENDING: 'ongoing',
  PENDING_LATE: 'ongoingLate',
  COMPLETED_ON_TIME: 'updated',
  COMPLETED_LATE: 'updatedLate',
  CLOSED_ON_TIME: 'completed',
  CLOSED_LATE: 'completedLate',
  // Donut ANA DURUM gösterir: grup, ailesinin gecikmesiz (temel) tonunu alır; gecikmiş ton sütun
  // grafiğindeki kırılıma kalır. Bu satırlar OLMADAN gruplar eşleşmeyip `neutral`e düşüyor ve donut
  // tamamen GRİ çiziliyordu (2026-08-07'de gözlendi).
  GROUP_PENDING: 'ongoing',
  GROUP_COMPLETED: 'updated',
  GROUP_CLOSED: 'completed',
  // 085 ile kullanımdan kalkan İptal statüsünün eski kayıtları (yalnız Raporum'da dilim üretir).
  CANCELLED: 'neutral',
}

/** Aciliyet Derecesi (REFERANS TYPE=URGENCY_LEVEL) id'leri. */
export const URGENCY_LEVEL = {
  NOT_URGENT: 380,
  NORMAL: 381,
  URGENT: 382,
} as const

/** Önem Derecesi (REFERANS TYPE=IMPORTANCE_LEVEL) id'leri. "Kritik" ölçütü = en yüksek (Çok Önemli). */
export const IMPORTANCE_LEVEL = {
  LESS_IMPORTANT: 390,
  NORMAL: 391,
  VERY_IMPORTANT: 392,
} as const

/** "Kritik" = en yüksek Önem Derecesi (Çok Önemli, 392) — hesaplama kaldırıldıktan sonraki ölçüt (053/068). */
export const CRITICAL_IMPORTANCE_LEVEL = IMPORTANCE_LEVEL.VERY_IMPORTANT

// NOT: Aciliyet/Önem Derecesi <b>adları</b> sabit i18n eşlemesiyle DEĞİL, REFERANS'tan çözülür — form/filtre
// seçenekleri GET /actions/levels'ten, satır/detay gösterimi ise sunucunun DTO'daki *LevelName alanlarından
// gelir (statusName ile aynı kalıp). Böylece REFERANS'ta ad değiştiğinde ekran da değişir.

export interface ActionListItem {
  id: number
  recordNumber: string
  meetingId: number | null
  meetingName: string | null
  name: string
  /**
   * Görev Detayı (Görev Detay ekranındaki "Görev Detayı" alanı, en çok 2000 karakter); boş olabilir.
   * Tabloda yalnız yönetici kolon kümesinde gösterilir (bkz. ActionsListPanel).
   */
  description: string | null
  /** "YYYY-MM-DD" (termin); null olabilir. */
  deadline: string | null
  statusId: number | null
  /** Durum adı — sunucuda REFERANS'tan çözülür (istemci eşlemesi/yarışı yok). */
  statusName: string | null
  /** Önem Derecesi id (REFERANS TYPE=IMPORTANCE_LEVEL); seçilmemişse null. */
  importanceLevelId: number | null
  /** Önem Derecesi adı — sunucuda çözülür (Excel dışa aktarımı için; istemci kendi eşlemesini de kullanır). */
  importanceLevelName: string | null
  /** Sorumlu(lar) — MINUTES_ASSIGNEE'den (sicil bazında çoklu olabilir). */
  assignees: MinutePerson[]
  /**
   * Alt aksiyon mu? (`PARENT_ID` dolu → true.) "Oluşturduklarım" (created) kök + alt aksiyonu birlikte içerir;
   * "Bana Atananlar" (assigned) yalnız ALT aksiyon (2026-08-06); Tüm Aksiyonlar/Takip Ettiklerim yalnız kök
   * döndürür → false (066/FR-020 + 071).
   */
  subAction: boolean
  /** "Gecikti-Güncellendi mi?" (IS_DELAYED_UPDATED = Evet; 066). Yalnız veri alanı — liste kolonu/filtresi 2026-08-06'da kaldırıldı. */
  delayedUpdated: boolean
  /**
   * Giriş yapan bu satırı silebilir mi? Ölçüt **tek yetkidir**: `RECORD_DELETE` (101, 2026-09-11).
   * 092'nin "sahip (`CRE_BY`) VEYA ayrıcalıklı" ikili modeli, dayandığı iki yetki katalogdan silindiği
   * için terk edildi — sahiplik ölçütü geri gelmedi.
   *
   * <p>**Kök ve alt görev satırı için aynı bayrak (2026-09-17):** alt satırlar eskiden daima `false`
   * alırdı (071). Hangi ucun çağrılacağını ekran {@link subAction} alanından bilir — kök görev ucu ya
   * da alt görev ucu (ikincisi üstteki Süreç Destek Yöneticisi atamasını da kaldırır).
   */
  deletable: boolean
  /**
   * Bu görevin alt görev sayısı (PARENT_ID = bu satır; silinmemiş, statüye bakılmaz). Sunucuda toplu
   * sayılır — liste "Alt Görev" kolonu. Alt görev satırlarında 0 gelir (alt görevin altına görev açılmaz).
   */
  subActionCount: number
  /**
   * Giriş yapan bu görevi takip ediyor mu (120 — satırdaki yıldız)? Alt görev satırında daima `false`.
   * Opsiyonel: eski bir backend alanı göndermez → takip edilmiyor sayılır (yıldız boş çizilir).
   */
  followed?: boolean
}

/** Aksiyon editör satırı (yerel durum). {@link id} mevcut aksiyon (düzenleme); null → yeni. */
export interface ActionFormRow {
  /** React liste anahtarı (yalnız istemci). */
  key: string
  id: number | null
  /** Aksiyon numarası — yalnız gösterim; yeni satırda boş (sunucu kayıtta üretir). */
  recordNumber: string
  name: string
  description: string
  /** Aciliyet Derecesi (URGENCY_LEVEL id) — toplantıya bağlı yeni aksiyonda zorunlu. */
  urgencyLevelId: number | null
  /** Önem Derecesi (IMPORTANCE_LEVEL id) — toplantıya bağlı yeni aksiyonda zorunlu. */
  importanceLevelId: number | null
  /** "YYYY-MM-DD" (termin). */
  deadline: string
  /** Durum id — yeni satırda "Aksiyon Bekleniyor" (330); mevcut satırda gerçek durum. Yalnız gösterim. */
  statusId: number | null
  /** Sorumlu(lar) — sicil bazında tekil. */
  assignees: MinutePerson[]
  /**
   * Satırın kaynağı — **yalnız istemci** (istek gövdesine girmez). Excel'den aktarılan satırlar
   * `'excel'` işaretlenir; ikinci aktarım yalnız o satırların yerine geçer, elle girilenlere dokunmaz
   * (088/FR-022). İşaretsiz satır elle girilmiş sayılır.
   */
  source?: 'manual' | 'excel'
  /**
   * Giriş yapan bu görevi takip ediyor mu (120 — yıldız)? **Yalnız gösterim**: istek gövdesine girmez
   * ({@link buildActionPayload}); takip ayrı uçtan değişir. Kaydedilmemiş satırda yoktur.
   */
  followed?: boolean
  /**
   * Kayıtlı satırın hedef teslim tarihi bu kullanıcıya açık mı (130)? **Yalnız gösterim**: istek gövdesine
   * girmez ({@link buildActionPayload}); sunucu senkronda aynı kuralla kapılar. Kaydedilmemiş (yeni)
   * satırda anlamsızdır — ilk tarih her zaman girilebilir.
   */
  deadlineEditable?: boolean
  /**
   * Stratejik Hedef / Katkı seçimi (127) — ad da tutulur ki pasife alınmış bir seçim pencerede adıyla
   * görünsün. **Tanımsızsa** payload'a hiç girmez ve sunucu görevin seçimini korur ("değişmedi"): Excel'den
   * gelen satır alanı bilmez, onun seçimi silmesi istenmez. Boş liste ise "temizle" demektir.
   */
  strategicObjectives?: LevelOption[]
  contributions?: LevelOption[]
}

/** POST/PUT gövdesindeki aksiyon (backend ActionInput ile birebir). */
export interface ActionInput {
  id?: number | null
  name: string
  description: string
  /** Aciliyet Derecesi (URGENCY_LEVEL id) — yeni aksiyonda zorunlu; mevcut (grandfathered) kayıtta null olabilir. */
  urgencyLevelId: number | null
  /** Önem Derecesi (IMPORTANCE_LEVEL id) — yeni aksiyonda zorunlu; mevcut kayıtta null olabilir. */
  importanceLevelId: number | null
  deadline: string
  /** Sorumlu(lar) — 0..n, sicil bazında tekil. */
  assignees: PersonInput[]
  /** Stratejik Hedef kimlikleri (127) — gönderilmezse görevin seçimi değişmez. */
  strategicObjectiveIds?: number[]
  /** Katkı kimlikleri (127) — gönderilmezse görevin seçimi değişmez. */
  contributionIds?: number[]
}

/** Referans picklist seçeneği (id + dile-duyarlı ad). */
export interface LevelOption {
  id: number
  name: string
}

/**
 * Görev formu picklist seçenekleri — GET /actions/levels: Aciliyet + Önem Derecesi ve (127) Stratejik
 * Hedef + Katkı. Dördü de yalnız Aktif kayıtlar, parametre ekranındaki sırayla.
 */
export interface ActionLevels {
  urgencyLevels: LevelOption[]
  importanceLevels: LevelOption[]
  strategicObjectives?: LevelOption[]
  contributions?: LevelOption[]
}

/** Görevin Stratejik Hedef / Katkı seçimi (127) — form alanlarının değeri. */
export interface TaskClassification {
  strategicObjectives: LevelOption[]
  contributions: LevelOption[]
}

/** Seçimsiz başlangıç değeri. */
export const EMPTY_CLASSIFICATION: TaskClassification = { strategicObjectives: [], contributions: [] }

/** Seçimi istek gövdesindeki kimlik listelerine çevirir. */
export function classificationIds(c: TaskClassification): {
  strategicObjectiveIds: number[]
  contributionIds: number[]
} {
  return {
    strategicObjectiveIds: c.strategicObjectives.map((o) => o.id),
    contributionIds: c.contributions.map((o) => o.id),
  }
}

/** Toplantı detayındaki aksiyon (backend MeetingActionDto ile birebir). */
export interface MeetingAction {
  id: number
  recordNumber: string
  name: string
  description: string | null
  urgencyLevelId: number | null
  importanceLevelId: number | null
  /** Önem Derecesi adı — sunucuda REFERANS'tan çözülür (istemci sabit eşlemesi yok). */
  importanceLevelName: string | null
  deadline: string | null
  statusId: number | null
  /** Durum adı — sunucuda REFERANS'tan çözülür (istemci eşlemesi/yarışı yok). */
  statusName: string | null
  /** Sorumlu(lar) — sicil bazında tekil. */
  assignees: MinutePerson[]
  /** Giriş yapan bu görevi takip ediyor mu (120 — yıldız)? Eski backend göndermez → `false` sayılır. */
  followed?: boolean
  /**
   * Giriş yapan bu görevin hedef teslim tarihini değiştirebilir mi (130 — `ACTION_DEADLINE_UPDATE` ∨
   * oluşturan)? Sunucu hesaplar; gelmezse **kapalı** sayılır (fail-closed).
   */
  deadlineEditable?: boolean
  /** Stratejik Hedef / Katkı (127) — satırın düzenleme penceresine kayıpsız taşınması için. */
  strategicObjectives?: LevelOption[]
  contributions?: LevelOption[]
}

/** Aksiyon içerik bölümü tipinin id'si (REF_MEETING_DECISION_TYPE: Aksiyon = 1). */
export const ACTION_DECISION_TYPE_ID = 1

/**
 * Editör satırlarını istek payload'ına çevirir. Tümüyle boş satır atlanır; bir alanı dolu ama zorunlusu eksik
 * satır → 'invalid' (çağıran uyarı gösterir). Sunucu ayrıca doğrular (skor aralığı, termin≥toplantı).
 */
export function buildActionPayload(rows: ActionFormRow[]): ActionInput[] | 'invalid' {
  const out: ActionInput[] = []
  for (const r of rows) {
    const anyFilled =
      r.name.trim() !== '' ||
      r.description.trim() !== '' ||
      r.urgencyLevelId != null ||
      r.importanceLevelId != null ||
      r.deadline !== ''
    if (!anyFilled) continue
    // Aciliyet/Önem Derecesi YALNIZ yeni aksiyonda (id yok) zorunlu; form bunu şart koşar. Kayıtlı
    // aksiyon (id dolu) bu kuraldan önce oluşmuş olabilir (derecesiz) — replace-all ile taşınırken
    // yeni aksiyon eklemeyi bloke etmesin (backend de aynı istisnayı uygular; termin/sorumlu kurallarıyla aynı gerekçe).
    const isNew = r.id == null
    // Ana Sorumlu (2026-08-12) YENİ aksiyonda zorunlu — derece/termin kurallarıyla aynı istisna: rolsüz
    // sorumluları olan eski kayıt (id dolu) replace-all ile taşınırken tutanağın kaydını bloke etmemeli.
    if (
      r.name.trim() === '' ||
      r.description.trim() === '' ||
      r.deadline === '' ||
      (isNew && (r.urgencyLevelId == null || r.importanceLevelId == null)) ||
      (isNew && mainResponsibleOf(r.assignees) == null)
    ) {
      return 'invalid'
    }
    out.push({
      id: r.id ?? undefined,
      name: r.name.trim(),
      description: r.description.trim(),
      urgencyLevelId: r.urgencyLevelId,
      importanceLevelId: r.importanceLevelId,
      deadline: r.deadline,
      assignees: r.assignees.map(minutePersonToInput),
      ...rowClassificationIds(r),
    })
  }
  return out
}

/**
 * Satırın seçimi (127) — yalnız satır alanı **taşıyorsa** gönderilir; taşımıyorsa (Excel satırı) alanlar
 * gövdeye girmez ve sunucu görevin seçimini korur.
 */
function rowClassificationIds(r: ActionFormRow): Pick<ActionInput, 'strategicObjectiveIds' | 'contributionIds'> {
  return {
    ...(r.strategicObjectives ? { strategicObjectiveIds: r.strategicObjectives.map((o) => o.id) } : {}),
    ...(r.contributions ? { contributionIds: r.contributions.map((o) => o.id) } : {}),
  }
}

/** Detay aksiyonu → editör satırı (düzenleme başlangıcı). */
export function actionToRow(a: MeetingAction): ActionFormRow {
  return {
    key: crypto.randomUUID(),
    id: a.id,
    recordNumber: a.recordNumber,
    name: a.name,
    description: a.description ?? '',
    urgencyLevelId: a.urgencyLevelId,
    importanceLevelId: a.importanceLevelId,
    deadline: a.deadline ?? '',
    statusId: a.statusId,
    assignees: a.assignees ?? [],
    followed: a.followed ?? false,
    deadlineEditable: a.deadlineEditable ?? false,
    // Eski backend alanı göndermezse tanımsız kalır → payload'a girmez → seçim korunur.
    strategicObjectives: a.strategicObjectives,
    contributions: a.contributions,
  }
}

/** HR seçimi (PersonInput) → yerel gösterim (MinutePerson): fullName ad+soyad'dan kurulur. */
export function personInputToMinutePerson(p: PersonInput): MinutePerson {
  const full = `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim()
  return {
    employeeId: p.employeeId,
    fullName: full || p.employeeId,
    firstName: p.firstName ?? null,
    lastName: p.lastName ?? null,
    jobTitleTr: p.jobTitleTr ?? null,
    jobTitleEn: p.jobTitleEn ?? null,
    businessMail: p.businessMail ?? null,
    assigneeTypeId: p.assigneeTypeId ?? null,
    // Liste etiketi kayıpsız taşınır: düşerse kişi listeden gelmiş olduğu hâlde tekil görünür ve
    // prensip kararı tablosunda grubundan kopardı (115).
    managerListId: p.managerListId ?? null,
  }
}

/** Kişiyi verilen sorumlu rolüyle etiketler (Ana/Yan seçicilerinin ortak yardımcısı). */
export function withAssigneeType(p: MinutePerson, assigneeTypeId: number): MinutePerson {
  return { ...p, assigneeTypeId }
}

/** Yerel gösterim (MinutePerson) → istek snapshot'ı (PersonInput). */
export function minutePersonToInput(p: MinutePerson): PersonInput {
  return {
    employeeId: p.employeeId,
    firstName: p.firstName,
    lastName: p.lastName,
    businessMail: p.businessMail,
    jobTitleTr: p.jobTitleTr,
    jobTitleEn: p.jobTitleEn,
    // Rol kayıpsız taşınır: rolsüz eski sorumlular replace-all senkronunda rolsüz gider ve silinmez.
    assigneeTypeId: p.assigneeTypeId ?? null,
    // Liste etiketi de kayıpsız: kayıt tamamı-değiştir (replace-all) semantiğiyle yazıldığı için,
    // taşınmazsa ilgisiz bir düzenleme listeden gelen herkesin bağını sessizce koparırdı (115).
    managerListId: p.managerListId ?? null,
  }
}

// ── Aksiyon Detay (analiz 7) ──

/** Aksiyon takipçisi (7.15) — MINUTES_FOLLOWER snapshot. */
export interface MinuteFollower {
  employeeId: string
  fullName: string
  jobTitleTr: string | null
  jobTitleEn: string | null
  businessMail: string | null
}

/**
 * Sorumlu / Paydaş gösterimi (backend MinutePersonDto ile birebir). {@link fullName} gösterim için;
 * {@link firstName}/{@link lastName} ayrıca tutulur ki aksiyon formunda kayıpsız düzenlenip geri gönderilebilsin.
 */
export interface MinutePerson {
  employeeId: string
  fullName: string
  firstName: string | null
  lastName: string | null
  jobTitleTr: string | null
  jobTitleEn: string | null
  businessMail: string | null
  /**
   * Sorumlu rolü — {@link ASSIGNEE_TYPE} (340 Ana / 341 Yan). **null olabilir**: 2026-08-12 öncesi
   * sorumluların rolü yoktur (geriye dönük doldurulmaz) ve paydaş/madde sorumlusunda rol kavramı yoktur.
   */
  assigneeTypeId?: number | null
  /**
   * Kişi bir **isim listesinden** eklendiyse o listenin kimliği (115); tek tek eklenen kişide null.
   * Prensip kararı tablosunda **gruplama anahtarıdır**.
   */
  managerListId?: number | null
  /** Listenin adı — gruplanmış satırda gösterilen metin. Sunucuda çözülür (115). */
  managerListName?: string | null
}

/** Aksiyon notu (7.8) — MINUTES_NOTE snapshot; değiştirilemez, yalnız metin. {@link creDate} eklenme zamanı (ISO). */
export interface ActionNote {
  id: number
  note: string
  /** Ekleyen kullanıcının ad-soyadı (CRE_BY çözülür); yoksa null. */
  authorName: string | null
  /** LocalDateTime/Instant ISO — eklenme tarih+saati. */
  creDate: string
  /** Son değişiklik (Instant ISO) — düzenlemede optimistic lock için gönderilir. */
  modDate: string
}

/** Alt aksiyon satırı (7.17). Atanan Kişi görünür-pasif → yer almaz. */
export interface SubAction {
  id: number
  recordNumber: string
  name: string
  /**
   * Alt görev açıklaması (`MEETING_MINUTES.DESCRIPTION`) — alt görev tablosunda kolon (2026-09-10).
   * 2026-09-09 öncesi açılmış alt görevlerde **boş** olabilir; 2000 karaktere kadar çıkabildiği için
   * hücre kırpılır ve tam metin `title` olarak taşınır.
   */
  description: string | null
  deadline: string | null
  statusId: number | null
  statusName: string | null
  /** Alt aksiyonun tek atananı (066/AK-5 — MINUTES_ASSIGNEE); yoksa null. */
  assignee: MinutePerson | null
  /** 066/7.16 — elle eklenen alt aksiyon (AUTOMATIC_MANUAL=Manuel) mi; yalnız manuel olanlar silinebilir. */
  manual: boolean
}

/** Aksiyon dokümanı (7.9) — backend ActionDocumentDto ile birebir (Toplantı MeetingDocument ile aynı yapı). */
export interface ActionDocument {
  id: number
  fileName: string
  size: number
  mimeType: string
  extension: string
  /** Yükleyen kullanıcının adı (CRE_BY çözülür); yoksa null. */
  uploadedByName: string | null
  /** Yüklenme zamanı (ISO). */
  uploadedAt: string
}

/** Aksiyon Detay yanıtı (backend ActionDetailDto ile birebir). */
export interface ActionDetail {
  id: number
  recordNumber: string
  name: string
  description: string | null
  deadline: string | null
  statusId: number | null
  statusName: string | null
  /** Aciliyet Derecesi (URGENCY_LEVEL id); seçilmemişse null. */
  urgencyLevelId: number | null
  /** Aciliyet Derecesi adı — sunucuda REFERANS'tan çözülür. */
  urgencyLevelName: string | null
  /** Önem Derecesi (IMPORTANCE_LEVEL id); seçilmemişse null. */
  importanceLevelId: number | null
  /** Önem Derecesi adı — sunucuda REFERANS'tan çözülür. */
  importanceLevelName: string | null
  /** Üst aksiyon (alt-aksiyonda dolu, kökte null). */
  parentId: number | null
  /** Üst aksiyonun termini (yalnız alt-aksiyonda dolu) — alt termin bundan ileri seçilemez (maxDate). */
  parentDeadline: string | null
  /**
   * Kullanıcı **üst görevi açabiliyor mu** (okuma kapsamı)? Kök görevde `false`.
   * Yalnız "Geri" butonunun yedek yolu için — `canSeeMainTask` ile karıştırma: ikisi yalnız
   * **kutu** koşuluyla ayrılır ve "Geri" kutuya bakmaz (kutu işaretsizken de kullanıcı ana görevi
   * kendi ilişkisiyle görebiliyor olabilir, geri dönebilmelidir).
   */
  parentVisible: boolean
  /**
   * Bu kullanıcıya **"Ana Görevi Gör"** butonu çizilsin mi?
   *
   * **Kayıt bayrağıdır.** İki koşul birden: `CAN_SEE_MAIN_TASK` "Hayır" değil · kullanıcı **ana
   * görevi açabiliyor** (`parentVisible` ile aynı ölçüt; kök görevde `false`). Kutu Evet ise
   * kısayol ana görevi gören **herkese** çizilir — alt görevin sorumlusu, ana görevin sorumlusu,
   * toplantı katılımcısı, takipçi, ayrıcalıklı roller.
   *
   * Bir tur KİŞİ bayrağıydı (üçüncü koşul "kullanıcı bu alt görevin sorumlusu"); aynı gün kullanıcı
   * kararıyla geri alındı: *"can see evet ise yetkisi olan herkes görecek"*.
   *
   * Kural **sunucuda** karar verilir, istemci yeniden hesaplaMAZ (`editable` ile aynı gerekçe):
   * ölçüt kayıt düzeyinde ilişkidir ve istemci onu göremez.
   */
  canSeeMainTask: boolean
  meetingId: number | null
  meetingName: string | null
  meetingLocation: string | null
  meetingDate: string | null
  /** Toplantı bitiş zamanı (ISO) — "Zaman" aralığı için; bağımsız aksiyonda null. */
  meetingEndDate: string | null
  /**
   * Tutanağı Düzenleyen — bağlı toplantının ENTERED_USER_ID → USERS görünen adı (sunucu çözer).
   * Yalnız toplantıya bağlı ANA görevde dolu; alt görevde ve bağımsız görevde null.
   */
  minutesEditorName: string | null
  followers: MinuteFollower[]
  /** Sorumlu(lar) — çoklu, sicil bazında tekil (MINUTES_ASSIGNEE). */
  assignees: MinutePerson[]
  subActions: SubAction[]
  documents: ActionDocument[]
  /** Aksiyon notları (7.8) — kronolojik (eskiden yeniye), değiştirilemez. */
  notes: ActionNote[]
  /**
   * 066/AK-10 — "Gecikti" ETİKETİ: statü Güncellendi (tüm alt aksiyonlar tamamlandı) iken ana aksiyonun
   * termini geçmişse true. Statü değişmez; yanına kırmızı "Gecikti" etiketi çizilir.
   */
  overdueLabel: boolean
  /** Kapanma anı (ISO instant) — statü **Kapatıldı**'ya çekilince yazılır; diğer statülerde null (085). */
  closureDate: string | null
  /**
   * **Onaya gönderme notu** (105) — görev 331'e çekilirken girilen açıklama; 29.09.2026'dan beri
   * ZORUNLUdur (eski kayıtlarda boş olabilir). Ana
   * görevde de alt görevde de sorulur; girilmediyse null. Yeniden açma bu alana **dokunmaz**.
   */
  completionNote: string | null
  /**
   * 087 — Termin yanındaki "güncelleme talebi" butonu gösterilsin mi. Üç koşulun (termin geçmemiş ·
   * statü uygun · bekleyen talep yok) **hepsi sunucuda** değerlendirilmiştir; istemci bu kuralı
   * yeniden hesaplamaz (066'da istemci↔uç ayrışması yaşandı).
   */
  deadlineRequestAllowed: boolean
  /** 087 — Bekleyen termin güncelleme talebinin id'si; yoksa null ("onay bekliyor" rozeti). */
  pendingDeadlineRequestId: number | null
  /**
   * Görevi oluşturan kullanıcının görünen adı (MEETING_MINUTES.CRE_BY → USERS). Sunucu çözer; kayıt
   * yoksa "#id" yedeği döner. Eski yanıtlara dayanıklı olmak için null kabul edilir.
   */
  createdByName: string | null
  /**
   * Kullanıcı bu görev üzerinde **işlem yapabilir** mi (2026-08-26). `false` → ekran salt-okuma.
   *
   * Ölçüt kayıt düzeyindedir (kullanıcının kayıtla ilişkisi: oluşturan/sorumlu/devreden ya da
   * ayrıcalıklı yetki). **Yalnız takipçi** olan kullanıcı `false` alır. İstemci bu kuralı yeniden
   * hesaplaMAZ — grant'lardan türetilemez ve iki yerde yazılırsa ayrışır (`deadlineRequestAllowed`
   * ile aynı gerekçe).
   */
  editable: boolean
  /**
   * Görevin **kimlik alanları** (Ana Sorumlu · Süreç Destek Yöneticisi · Takip Eden · başlık · detay)
   * bu kullanıcıya açık mı (2026-08-31)? `editable`'dan **daha dardır**: görevin **Ana Sorumlusu**
   * kayıt üzerinde işlem yapar (statü, termin talebi, not, alt görev, doküman) ama kendi
   * görevinin tanımını değiştiremez.
   *
   * <p>Muafiyet iki yoldan: **yönetim** (`MINUTES_CREATE`) ve görevi **oluşturan** kullanıcı.
   * İkincisi istemcide hesaplanamaz — yanıtta yalnız oluşturanın **görünen adı** var ve ada göre
   * kıyas kırılgandır; bu yüzden alan `editable` gibi sunucudan gelir ve burada yeniden
   * hesaplanMAZ.
   */
  infoEditable: boolean
  /**
   * Kök görevin **hedef teslim tarihi** bu kullanıcıya doğrudan değiştirilebilir mi (2026-10-02)?
   * Görevin **atananı** (Ana Sorumlu / Süreç Destek Yöneticisi — Yönetici rolündekiler dahil)
   * `false` alır; onun yolu termin güncelleme talebidir (087). Muaf: yönetim (`MINUTES_CREATE`),
   * görevi oluşturan ve kayda yazabilen ama atanan olmayan kullanıcı (ör. Genel Müdür); alt görevde
   * yalnız ilk ikisi. `infoEditable` ile aynı gerekçeyle sunucudan gelir, burada hesaplanMAZ.
   */
  deadlineEditable: boolean
  /** Optimistic lock (güncelleme/statü isteğinde geri gönderilir). */
  modDate: string
  /**
   * Stratejik Hedef / Katkı (127) — görevde seçili olanlar, kayıt sonradan pasif/silinmiş olsa da;
   * parametre ekranındaki sırayla. Alt görevde boş.
   */
  strategicObjectives?: LevelOption[]
  contributions?: LevelOption[]
}

/** PUT /actions/{id} gövdesi (tanım/açıklama/termin + opsiyonel not). */
export interface ActionUpdateRequest {
  name: string
  description: string
  /** "YYYY-MM-DD". */
  deadline: string
  /** Aksiyon notu — termin değiştiyse zorunlu (7.8); değişmediyse opsiyonel. Boş → gönderilmez. */
  note?: string | null
  modDate: string
  /** Stratejik Hedef / Katkı kimlikleri (127) — gönderilmezse değişmez, boş liste temizler. */
  strategicObjectiveIds?: number[]
  contributionIds?: number[]
}

/** POST /actions/{id}/subactions gövdesi. */
export interface SubActionCreateRequest {
  name: string
  /**
   * Alt görev açıklaması — <b>isteğe bağlı</b>, en çok 2000 karakter (kullanıcı kararı 2026-09-14;
   * 2026-09-09'da ana görevle aynı kurala bağlanıp zorunlu tutulmuştu). Boş bırakılırsa boş dizge gider.
   * ANA görev açıklaması zorunlu KALIR — iki form bilinçli olarak ayrışır.
   */
  description: string
  deadline: string
  /**
   * Alt görevin sorumluları — en az bir kişi zorunlu (FR-004). **Her kişi için ayrı bir alt görev
   * açılır** (kullanıcı isteği 20.10.2026); diğer alanlar hepsinde ortaktır ve her alt görevin tek
   * atananı kalır (066/AK-5). Sunucu hepsini tek transaction'da yazar; üst sınırı aşan istek 400
   * `tooManySubActionAssignees` alır (sayı istemcide tutulmaz — tek tanım backend `MeetingLimits`).
   *
   * Bu, ekranın kullandığı biçimdir; **kabloya** tek sorumluda eski `assignee` (tek nesne) olarak
   * çevrilir — eski backend uyumu için, bkz. `actionApi.createSubAction`.
   */
  assignees: PersonInput[]
  /**
   * "Atanan kişi ana görevi de görebilsin" onay kutusu (2026-09-10). `true` → sorumlu ana görevin
   * detayını salt-okuma açabilir; `false` → ana görevi de, kardeş alt görevleri de, görev
   * toplantıdan geliyorsa tutanağı da göremez.
   *
   * Sunucu bunu `REFERANS(YES_NO)` kimliğine çevirir (11=Evet / 10=Hayır); istemci referans
   * kimliği taşımaz (`isProcessed` ile aynı desen).
   */
  canSeeMainTask: boolean
}

/**
 * PUT /actions/{id}/subaction gövdesi — ayrı Alt Aksiyon Detay sayfası alan güncellemesi (066). Yalnız
 * Tanım + Açıklama + Hedef Teslim Tarihi düzenlenir; Atanan DEĞİŞMEZ (üst aksiyonun sorumlusuyla senkron — AK-5), not/doküman YOK (AK-7).
 */
export interface SubActionUpdateRequest {
  name: string
  /** Alt görev açıklaması — oluşturmadaki gibi <b>isteğe bağlı</b>, en çok 2000 karakter (2026-09-14). */
  description: string
  deadline: string
  /**
   * Alt gorev notu (2026-08-28) — hedef teslim tarihi degistiyse **zorunlu**, aksi halde istege bagli;
   * kural ana gorevle birebir ayni. Bos -> gonderilmez.
   */
  note?: string
}

/**
 * POST /actions gövdesi — toplantıdan bağımsız aksiyon oluşturma (analiz 6.5.3). Toplantı/yer ilişkisi yok;
 * Tanım + Açıklama + Sorumlu(lar) (çoklu) + Termin. Aciliyet/Önem
 * Derecesi OPSİYONELdir (toplantıya bağlı aksiyonda zorunlu; burada boş bırakılabilir) — verilirse geçerli
 * bir referans değeri olmalıdır.
 */
export interface StandaloneActionCreateRequest {
  // Kayıt grubu (122) gövdede yok: sunucu oluşturanın üyeliğinden türetir (19.10.2026).
  name: string
  description: string
  /** "YYYY-MM-DD" (termin); geçmiş tarih olamaz. */
  deadline: string
  /** Sorumlu(lar) — en az bir kişi zorunlu; sicil bazında tekil. */
  responsibles: PersonInput[]
  /** Aciliyet Derecesi (URGENCY_LEVEL id) — opsiyonel; null → seçilmedi. */
  urgencyLevelId?: number | null
  /** Önem Derecesi (IMPORTANCE_LEVEL id) — opsiyonel; null → seçilmedi. */
  importanceLevelId?: number | null
  /** Stratejik Hedef / Katkı kimlikleri (127) — opsiyonel, çoklu. */
  strategicObjectiveIds?: number[]
  contributionIds?: number[]
}

/** Takip durumu — `PUT`/`DELETE /actions/{id}/follow` yanıtı (120; backend ActionFollowStateDto). */
export interface ActionFollowState {
  followed: boolean
}

/**
 * HR snapshot girdisi — kişi seçicilerin ortak şekli ({@link PersonInput}'un tabanı). Adı tarihseldir:
 * takipçi ekleme ucu (`POST /actions/{id}/followers`) 120'de kalktı, kişi kendini yıldızla takibe alır.
 */
export interface FollowerInput {
  employeeId: string
  firstName?: string | null
  lastName?: string | null
  businessMail?: string | null
  jobTitleTr?: string | null
  jobTitleEn?: string | null
}

/**
 * HR snapshot girdisi — Sorumlu (POST /assignees); FollowerInput ile aynı şekil + opsiyonel sorumlu rolü.
 * {@link assigneeTypeId} yalnız **aksiyon sorumlusu** bağlamında anlamlıdır (takipçi/katılımcı/paydaşta yok).
 */
export type PersonInput = FollowerInput & {
  assigneeTypeId?: number | null
  /**
   * Kişi bir **isim listesinden** eklendiyse o listenin kimliği (115). İstemciden gelir ve bu
   * bilinçlidir: hangi listenin seçildiği bir **kullanıcı kararıdır**, sunucunun yeniden
   * türetebileceği bir veri değil. Sunucu yine de doğrular (var olmayan/silinmiş liste reddedilir).
   */
  managerListId?: number | null
}

/** MINUTES_ASSIGNEE_TYPE (REFERANS) — sorumlu rolü. Analist 2026-08-12'de ekledi (check constraint). */
export const ASSIGNEE_TYPE = { MAIN: 340, SECONDARY: 341 } as const

/**
 * Bir aksiyonun **Ana Sorumlusu** (tekil) — yoksa null. Rolsüz (2026-08-12 öncesi) kayıtlar Ana sayılmaz;
 * o aksiyonlarda Ana alanı boş görünür ve kullanıcı atayana kadar boş kalır (kullanıcı kararı).
 */
export function mainResponsibleOf(people: MinutePerson[] | null | undefined): MinutePerson | null {
  return (people ?? []).find((p) => p.assigneeTypeId === ASSIGNEE_TYPE.MAIN) ?? null
}

/**
 * Bir **alt aksiyon** satırının Sorumlusu — alt aksiyonun tek atananı (066/AK-5); yoksa null.
 * {@link mainResponsibleOf}'tan ayrı bir ölçüt kullanır ve rol filtresi **uygulamaz**: alt aksiyonun
 * atananı üretimde Ana rolüyle yazılır (`AutoSubActionService`), ama 2026-08-12 öncesi kayıtlarda rol
 * **boştur** ve canlı veride çoğunluk odur → role bakan ölçüt bu satırlarda hücreyi boş bırakıyordu
 * (kullanıcı bildirimi 2026-08-18). Sunucunun alt aksiyon detayındaki ölçütüyle aynı sonucu verir:
 * orada "tek atanan (ilk)" alınır (`ActionDetailService.subActionAssignees`; alt aksiyon detay ekranı da
 * `assignees[0]`). **Tam eşitlik değil:** sunucu role hiç bakmaz, bu ölçüt Yan(341) satırları atlar — iki
 * uç ancak alt aksiyonun İLK atananı 341 ise ayrışır. Alt aksiyonun tek atananı üretimde Ana/rolsüz yazılır,
 * arayüzde alt aksiyona Yan Sorumlu eklenemez → pratikte ayrışma yok; ayrışsa da bu ölçüt daha doğrudur
 * (Yan Sorumlu kendi kolonunda görünür).
 *
 * <p><b>Yan Sorumlular dışlanır</b> — onlar kendi kolonunda listelenir; dışlanmasa aynı ad tek satırda
 * iki hücrede görünürdü. Kök aksiyonda kullanılmaz: orada rolsüz sorumlunun hücreyi boş bırakması
 * bilinçli bir karardır (bkz. {@link mainResponsibleOf}).
 */
export function subActionResponsibleOf(
  people: MinutePerson[] | null | undefined,
): MinutePerson | null {
  return (people ?? []).find((p) => p.assigneeTypeId !== ASSIGNEE_TYPE.SECONDARY) ?? null
}

/** Bir aksiyonun **Yan Sorumluları** (0..n), ekleme sırasında. */
export function secondaryResponsiblesOf(people: MinutePerson[] | null | undefined): MinutePerson[] {
  return (people ?? []).filter((p) => p.assigneeTypeId === ASSIGNEE_TYPE.SECONDARY)
}

/**
 * Verilen sicil bu listede **Ana Sorumlu** mu? Aynı kişinin hem Ana hem Yan seçilmesini engellemek için
 * kullanılır (kullanıcı kuralı 2026-08-12): çakışma sessizce çözülmez, seçim reddedilir.
 */
export function isMainResponsible(
  people: MinutePerson[] | null | undefined,
  employeeId: string,
): boolean {
  return (people ?? []).some(
    (p) => p.employeeId === employeeId && p.assigneeTypeId === ASSIGNEE_TYPE.MAIN,
  )
}

/** Verilen sicil bu listede **Yan Sorumlu** mu? (bkz. {@link isMainResponsible} — aynı kural, simetriği) */
export function isSecondaryResponsible(
  people: MinutePerson[] | null | undefined,
  employeeId: string,
): boolean {
  return (people ?? []).some(
    (p) => p.employeeId === employeeId && p.assigneeTypeId === ASSIGNEE_TYPE.SECONDARY,
  )
}

/**
 * **Rolsüz** sorumlular — 2026-08-12 öncesi kayıtlar. Ayrı gösterilir ki Ana/Yan bölümleri yanıltmasın ve
 * kayıt düzenlenirken sessizce kaybolmasınlar (backend de rolsüz taşımayı destekler).
 */
export function unroledResponsiblesOf(people: MinutePerson[] | null | undefined): MinutePerson[] {
  return (people ?? []).filter(
    (p) => p.assigneeTypeId !== ASSIGNEE_TYPE.MAIN && p.assigneeTypeId !== ASSIGNEE_TYPE.SECONDARY,
  )
}

/**
 * Aksiyon alt-sayfa kapsamı (analiz 6). Bana Atananlar (assigned) = giriş yapanın sicili
 * MINUTES_ASSIGNEE'de.
 *
 * <p>`delegation` (2026-08-30) = **assigned ∪ devrettiklerim**. Anasayfa'daki Delegasyon Özeti kartının
 * "Tümünü Gör" hedefi; ekran "Bana Atananlar" ile **aynı panelidir** (süzgeçler/kolonlar/Excel), tek
 * farkı kümenin genişliği ve "Tür" kolonudur. **Sekme DEĞİLDİR** — `ACTION_TAB_SCOPES`'a bilinçle
 * eklenmedi: menüde yoktur ve `/minutes/actions` kök yönlendirmesi oraya düşmemelidir.
 */
export type ActionScope = 'all' | 'created' | 'assigned' | 'followed' | 'delegation'

/**
 * Kişi süzgecinin rolü — seçenek ucuna gönderilen değer (`?role=`).
 *
 * `'support'` arayüzde **Süreç Destek Yöneticisi**, veritabanında **Yan Sorumlu** (rol 341) demektir;
 * bu ayrışma bilinçlidir (bkz. CLAUDE.md → Arayüz Terminolojisi ↔ DB Verisi Ayrışması).
 */
export type ResponsibleRole = 'main' | 'support'

/**
 * Aksiyon alt sayfalarının kapsam sırası (sol menü "Aksiyonlar" başlığı altındaki öğe sırası; grup kökü
 * `/minutes/actions` ilk yetkili kapsama bu sırayla yönlendirir). Her kapsam ayrı rotadır (`/minutes/actions/<scope>`).
 */
export const ACTION_TAB_SCOPES: ActionScope[] = ['all', 'created', 'assigned', 'followed']

/**
 * Anasayfa panosunun "Tümünü Gör" bağlantılarının taşıdığı işaretçi (2026-08-21). Değeri anlamsızdır;
 * varlığı "görünümü URL belirliyor, `sessionStorage`'daki son görünümü kullanma" demektir (bkz.
 * `ActionsListPanel.hasDeepLinkFilters`). Sunucuya gönderilmez.
 */
export const DASHBOARD_LINK_PARAM = 'fromDashboard'

export interface ActionQuery {
  /** Alt-sayfa kapsamı; verilmezse 'all' (Tüm Aksiyonlar). */
  scope?: ActionScope
  q?: string
  importanceLevelId?: number | null
  statusIds?: number[]
  /**
   * Yalnız **açık** ve (önemi **Kritik** VEYA termini geçmiş) aksiyonlar (053). Anasayfa panosundaki
   * "Kritik ve Geciken Aksiyonlar" bloğunun ölçütüyle aynıdır; pano bu filtreyi URL üzerinden gönderir.
   * Verilmezse davranış eskisi gibidir (geriye uyumlu).
   */
  criticalOrOverdue?: boolean
  /**
   * Yalnız **açık ve termini geçmiş** aksiyonlar (2026-08-21). `criticalOrOverdue`'nun önemi dışarıda
   * bırakan hâli: Raporum'un **"Geciken Aksiyonlar"** bloğunun ölçütü tam olarak budur ve o bloğun
   * "Tümünü Gör" bağlantısı bunu gönderir. (Blok 2026-08-19'da "Kritik ve Geciken"den "Geciken"e daraldı;
   * bağlantı eski süzgeçte kaldığı için kritik-ama-gecikmemiş kayıtları da açıyordu.) Sunucuda parametre
   * adı `overdue`.
   */
  overdueOnly?: boolean
  /**
   * Yalnız **açık ve termini bugün/sonrası** aksiyonlar (2026-08-21). Raporum'un **"Kalan Süre
   * Dağılımı"** bloğunun ölçütüdür (o blok gecikmişleri ve terminsizleri saymaz) → o bloğun "Tümünü Gör"
   * bağlantısı bunu gönderir. Sunucuda parametre adı `upcoming`.
   */
  upcomingOnly?: boolean
  /**
   * Yalnız **devrettiklerim** = aksiyonun **son** devir kaydında devreden ben olanlar — "bendeyken kime
   * devrettim" (2026-08-23; öncesi "başkasına atanmış ∧ bana atanmamış"). Oluşturup birine atamak bu
   * kümeye **girmez**. Sunucuda parametre adı `delegated` ve iki koşulu birlikte değiştirir: kapsamın
   * "benim oluşturduğum" koşulu uygulanmaz (devrettiğim işi ben oluşturmamış olabilirim) ve dönem
   * penceresi aksiyonun değil **devrin** tarihine bakar.
   */
  delegatedOnly?: boolean
  /**
   * Panonun **dönem penceresi** (2026-08-21) — oluşturma tarihine (`CRE_DATE`) uygulanır. Dönem → tarih
   * aralığı çevrimini **sunucu** yapar (panoyla aynı kaynak: `DashboardRange`), bu yüzden istemci hazır
   * tarih değil dönem anahtarı gönderir. `CUSTOM` iken `periodFrom`/`periodTo` zorunludur.
   *
   * <p>Bu süzgeç olmadan "Tümünü Gör" panonun bir ayını sayan bir karttan **tüm zamanları** gösteren bir
   * listeye götürüyordu (kullanıcı bildirimi: kartta 3, tabloda 5 satır).
   */
  period?: string
  periodFrom?: string | null
  periodTo?: string | null
  /** Tür filtresi (071 — yalnız Oluşturduklarım/Bana Atananlar): null/undefined = hepsi; false = yalnız ana (kök); true = yalnız alt aksiyon. */
  subAction?: boolean | null
  /** "Geciken" filtresi (arşiv) — true → yalnız IS_DELAYED_UPDATED=Evet satırlar; yoksa süzme yok. */
  delayedUpdated?: boolean
  /**
   * Kişi süzgeçleri — 2026-09-15'te **ayrıldı** (önce tek alan Ana VEYA Yan rolünde arıyordu). Her biri
   * yalnız kendi rolünde eşleşir ve ikisi **VE** ile birleşir. Rolsüz (rol alanı boş) sorumlular
   * hiçbirine girmez. Boş/undefined → o süzgeç uygulanmaz.
   */
  /** Ana Sorumlu (rol 340) sicili. Süreç Destek Yöneticisi süzgecinden **ayrıdır**; ikisi VE ile birleşir. */
  mainResponsibleEmployeeId?: string | null
  /** Süreç Destek Yöneticisi sicili (DB'de "Yan Sorumlu", rol 341). */
  supportManagerEmployeeId?: string | null
  page?: number
  size?: number
  /** Spring sort: "field,dir" (ör. "deadline,asc"). */
  sort?: string
}

/**
 * Kalan süre (takvim günü): termin − bugün. Sonlanmış/kapanmış durumlarda kalan süre anlamsız → null
 * (boş gösterilir): Tamamlandı / İptal / Güncellendi ve bunların gecikmeli çeşitleri — Tamamlandı (Gecikmeli)
 * ve Güncellendi (Gecikmeli). "Devam Eden (Gecikmeli)" (332) AÇIKtır → dışarıda; kalan (negatif) gösterilir.
 * Termin yoksa null. Gelecek pozitif, bugün 0, geçmiş negatif.
 */
export function remainingDays(deadline: string | null, statusId: number | null): number | null {
  if (
    !deadline ||
    statusId === ACTION_STATUS.COMPLETED ||
    statusId === ACTION_STATUS.CLOSED ||
    statusId === ACTION_STATUS.ARCHIVED
  ) {
    return null
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(deadline)
  if (!m) return null
  const due = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  // "Bugün" KURUMSAL gündür (todayStart → Europe/Istanbul), tarayıcının yerel günü DEĞİL. Aynı ekranda
  // gecikme kararı (isPastDeadline) da bu günden okunur; ikisi ayrışırsa Termin Bilgi Kartı "Bugün" derken
  // sayfa aynı anda Gecikti akışını başlatır ve kullanıcı çelişkiyi anlamlandıramaz.
  const today = todayStart()
  return Math.round((due.getTime() - today.getTime()) / 86400000)
}
